import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { frontendDb as db } from './lib/frontend-db';
import Header from './components/Header';
import Slideshow from './components/Slideshow';
import NewsSection from './components/NewsSection';
import PostersSection from './components/PostersSection';
import VereineSection from './components/VereineSection';
import Footer from './components/Footer';
import CustomSection from './components/CustomSection';
import CookieConsent from './components/CookieConsent';
import DynamicFontLoader from './components/DynamicFontLoader';
import DynamicFavicon from './components/DynamicFavicon';
import ErrorBoundary from './components/ErrorBoundary';
import YoungStarsSection from './components/YoungStarsSection';
import ContactFormSection from './components/ContactFormSection';
import SEOHead from './components/SEOHead';
import type { GalleryItem, CustomSection as CustomSectionType, ContactInfo } from './lib/frontend-db';

function App() {
  const { pathname, hash } = useLocation();
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null);
  const [, setPrimaryColor] = useState('#a2bba3');
  const [customSections, setCustomSections] = useState<CustomSectionType[]>([]);
  const [aboutContent, setAboutContent] = useState('');
  const [aboutTitle, setAboutTitle] = useState('Das sind wir!');
  const [galleryTitle, setGalleryTitle] = useState('Galerie');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoAlt, setLogoAlt] = useState('StimmArt Logo');
  const [settingsData, setSettingsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contact, setContact] = useState<ContactInfo | null>(null);

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Set default values first
      setPrimaryColor('#a2bba3');
      setLogoUrl('');
      setLogoAlt('StimmArt Logo');
      setGallery([]);
      setCustomSections([]);
      setAboutContent('Willkommen beim Liederkranz Bieber!');
      setSettingsData({
        primary_color: '#a2bba3',
        logo_url: '',
        logo_alt: 'StimmArt Logo'
      });

      // Set CSS variables
      document.documentElement.style.setProperty('--primary-color', '#a2bba3');

      // Load data individually to handle partial failures gracefully
      try {
        const galleryData = await db.getGallery() as unknown as GalleryItem[];
        setGallery(galleryData);
      } catch (error) {
        console.error('Error loading gallery:', error);
        // Keep empty array
      }

      try {
        const settingsData = await db.getSettings() as any;
        if (settingsData) {
          setPrimaryColor(settingsData.primary_color);
          setLogoUrl(settingsData.logo_url || '');
          setLogoAlt(settingsData.logo_alt || 'StimmArt Logo');
          setGalleryTitle(settingsData.gallery_title || 'Galerie');
          setSettingsData(settingsData);
          document.documentElement.style.setProperty('--primary-color', settingsData.primary_color);
          
          // Apply font family if set
          if (settingsData.font_family && settingsData.font_family !== 'System Default') {
            const fontFamilyWithFallbacks = `"${settingsData.font_family}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
            
            // Set CSS variables and body font
            document.documentElement.style.setProperty('--font-family', fontFamilyWithFallbacks);
            document.body.style.fontFamily = fontFamilyWithFallbacks;
            
            // Add global CSS to ensure font is applied everywhere
            const fontStyle = document.createElement('style');
            fontStyle.id = 'global-font-style';
            fontStyle.textContent = `
              *, *::before, *::after {
                font-family: ${fontFamilyWithFallbacks} !important;
              }
            `;
            
            // Remove existing global font style if present
            const existingStyle = document.getElementById('global-font-style');
            if (existingStyle) {
              existingStyle.remove();
            }
            
            document.head.appendChild(fontStyle);
            console.log('✅ Applied global font family:', fontFamilyWithFallbacks);
          } else {
            // Remove global font style if using system default
            const existingStyle = document.getElementById('global-font-style');
            if (existingStyle) {
              existingStyle.remove();
            }
            console.log('🔄 Using system default font');
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        // Keep default settings
      }

      try {
        const sectionsData = await db.getCustomSections() as unknown as CustomSectionType[];
        setCustomSections(sectionsData.sort((a: any, b: any) => a.order_index - b.order_index));
      } catch (error) {
        console.error('Error loading custom sections:', error);
        // Keep empty array
      }

      try {
        const aboutData = await db.getAbout() as any;
        if (aboutData) {
          setAboutContent(aboutData.content);
          setAboutTitle(aboutData.section_title || 'Das sind wir!');
        }
      } catch (error) {
        console.error('Error loading about data:', error);
        // Keep default content
      }

      try {
        const contactData = await db.getContact() as ContactInfo | null;
        if (contactData) {
          setContact(contactData);
        }
      } catch (error) {
        console.error('Error loading contact data:', error);
        // Keep null to fall back to defaults in render
      }

    } catch (error) {
      console.error('Error loading data:', error);
      // Don't set error state, just use defaults
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="h-[600px] mt-16 bg-gray-100 animate-pulse" />
        <div className="py-16 space-y-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-gray-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-16 mt-16">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Fehler</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const galleryCount = gallery.length;
  const isSingleGallery = galleryCount === 1;

  return (
    <ErrorBoundary>
      <SEOHead
        organizationData={{
          name: 'StimmArt',
          foundingDate: '2023',
          email: contact?.email || 'info@stimmart-chor.de',
          url: 'https://stimmart-chor.de',
          logo: logoUrl || undefined,
          socialMedia: {
            facebook: settingsData?.facebook_url,
            instagram: settingsData?.instagram_url
          }
        }}
      />
      <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <Header />
        <main>
        <Slideshow />
        
        {/* Logo Section */}
        {logoUrl && (
          <section className="py-12" style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <div className="container mx-auto px-4">
              <div className="flex justify-center">
                <img 
                  src={logoUrl} 
                  alt={logoAlt}
                  className="max-h-32 w-auto object-contain"
                />
              </div>
            </div>
          </section>
        )}
        
        <section id="about" className="py-16 dark:bg-slate-900 transition-colors">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">{aboutTitle}</h2>
            
            {/* YouTube Video (if set) */}
            {settingsData?.about_youtube_url && (
              <div className="max-w-3xl mx-auto mb-8">
                <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                    src={settingsData.about_youtube_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    title="StimmArt Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>
            )}
            
            <div className="max-w-3xl mx-auto space-y-4 text-center" style={{ color: 'var(--text-primary)' }}>
              {aboutContent.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4">{paragraph}</p>
              ))}
              
              {/* Social Media Links */}
              {(settingsData?.facebook_url || settingsData?.instagram_url) && (
                <div className="flex justify-center items-center space-x-8 mt-8">
                  {settingsData?.facebook_url && (
                    <a
                      href={settingsData.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center"
                      title="Besuchen Sie uns auf Facebook"
                    >
                      <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                  )}
                  
                  {settingsData?.instagram_url && (
                    <a
                      href={settingsData.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pink-600 hover:text-pink-800 transition-colors duration-200 flex items-center"
                      title="Folgen Sie uns auf Instagram"
                    >
                      <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24" style={{ transform: 'translateY(1px)' }}>
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Veranstaltungen */}
        <PostersSection />

        {/* Aktuelles */}
        <NewsSection />

        {/* Eigene Sektionen */}
        <CustomSection sections={customSections} />

        {/* YoungStars Sektion - Dauerhaft aktiv, wenn Titel vorhanden */}
        {settingsData?.youngstars_title && (
          <YoungStarsSection
            enabled={true}
            title={settingsData.youngstars_title || ''}
            logoUrl={settingsData.youngstars_logo_url || ''}
            content={settingsData.youngstars_content || ''}
            gallery={settingsData.youngstars_gallery ? JSON.parse(settingsData.youngstars_gallery) : []}
          />
        )}

        {/* Galerie */}
        <section id="gallery" className="py-16 dark:bg-slate-900 transition-colors">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">{galleryTitle}</h2>
            <div className={
              isSingleGallery
                ? "flex justify-center"
                : galleryCount === 2
                  ? "flex justify-center gap-8"
                  : galleryCount === 3
                    ? "flex justify-center gap-8"
                    : "flex flex-wrap justify-center gap-8"
            }>
              {gallery.map((item) => (
                <div
                  key={item.id}
                  className={(isSingleGallery ? "max-w-[360px] w-full" : "w-[360px]") + " cursor-pointer"}
                  onClick={() => {
                    if (item.link_url) {
                      window.open(item.link_url, '_blank', 'noopener,noreferrer');
                    } else {
                      setSelectedImage(item);
                    }
                  }}
                >
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-64 object-cover rounded-lg shadow-md hover:opacity-90 transition-opacity"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vereine */}
        <VereineSection />

        {/* Kontaktformular */}
        <ContactFormSection />

        <section id="contact" className="py-16 dark:bg-slate-900 transition-colors" style={{ backgroundColor: 'var(--bg-secondary)' }}>
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">{contact?.section_title || 'Kontakt'}</h2>
            <div className="max-w-xl mx-auto">
              <div className="rounded-lg shadow-md p-8" style={{ backgroundColor: 'var(--bg-primary)' }}>
                {contact ? (
                  <div className="text-center" style={{ color: 'var(--text-primary)' }}>
                    {contact.rehearsal_location && (
                      <p className="mb-4">
                        <strong>{contact.rehearsal_location_label || 'Probenort:'}</strong><br />
                        <span className="whitespace-pre-line">{contact.rehearsal_location}</span>
                      </p>
                    )}
                    {contact.rehearsal_time && (
                      <p className="mb-4">
                        <strong>{contact.rehearsal_time_label || 'Probenzeit:'}</strong><br />
                        {contact.rehearsal_time}
                      </p>
                    )}
                    {contact.email && (
                      <p className="mb-0">
                        <strong>{contact.email_label || 'E-Mail:'}</strong><br />
                        <a href={`mailto:${contact.email}`} className="text-[var(--primary-color)] hover:underline">
                          {contact.email}
                        </a>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center" style={{ color: 'var(--text-primary)' }}>
                    <p className="mb-4">
                      <strong>Probenort:</strong><br />
                      Sängerhütte\n63831 Wiesen
                    </p>
                    <p className="mb-4">
                      <strong>Probenzeit:</strong><br />
                      Jeden Donnerstag von 19:30 Uhr bis 21:00 Uhr
                    </p>
                    <p className="mb-0">
                      <strong>E-Mail:</strong><br />
                      <a href="mailto:info@liederkranz-bieber.de" className="text-[var(--primary-color)] hover:underline">
                        info@liederkranz-bieber.de
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="max-w-4xl w-full">
              <img 
                src={selectedImage.image_url} 
                alt={selectedImage.title}
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}
        </main>
        <Footer />
        <CookieConsent />
        <DynamicFontLoader />
        <DynamicFavicon />
      </div>
    </ErrorBoundary>
  );
}

export default App;
